<template>
  <div>
    <div class="offers-header has-text-centered">
      <p class="is-size-4 is-size-3-desktop has-text-weight-bold">
        {{ $t("af:offers.title") }}
      </p>

      <span
        id="offersBackToPrelim"
        class="retake-assessment cursor-pointer"
        @click="retakeAssessment"
      >
        <b-icon
          icon-pack="fa"
          icon="chevron-left"
          type="is-blue"
          size="is-small"
          class="mr-2"
        />

        <span class="text is-size-14 has-text-weight-medium">
          {{ $t("af:offers.button.back_to_preliminary") }}</span
        >
      </span>
    </div>

    <div class="container">
      <div class="af-card">
        <p class="has-text-weight-bold mb-5">
          {{ $t("af:offers.filter.title") }}
        </p>

        <div class="af-card__filter">
          <!--Amount-->
          <div>
            <validation-observer ref="observer">
              <BInputWithValidation
                id="offersAmountInput"
                :label="$t('af:offers.filter.input.amount.label')"
                :mask="FIELD_MASK.HUF"
                :rules="{ required: true, regex: /^[0-9 ]*$/ }"
                label-position="on-border"
                :value="getAmount"
                @blur="adjustAmount"
                ref="amountInput"
                :show-success="false"
              >
              </BInputWithValidation>
            </validation-observer>

            <div class="loan-min-max has-text-grey-icon is-size-7">
              <span> Min. {{ monify(filterSettings.amountMin) }} HUF</span>
              <span> Max. {{ monify(filterSettings.amountMax) }} HUF</span>
            </div>
          </div>

          <!--Term-->
          <TermSlider v-model="duration" />

          <!--Actions-->
          <div>
            <b-button type="is-blue" expanded @click="recalculate">{{
              $t("af:offers.filter.button.recalculate")
            }}</b-button>
            <span
              class="af-text-button af-text-button--expanded has-text-blue has-text-weight-medium"
              @click="restoreFilters"
              >{{ $t("af:offers.filter.button.reset") }}</span
            >
          </div>
        </div>

        <div v-if="activePurchase" class="mt-5">
          <p class="has-text-weight-bold mb-3">
            {{ $t("af:offers.filter.purchase.title") }}
          </p>

          <div class="purchase-card-container">
            <div class="purchase-card">
              <p class="purchase-card-label">
                {{ $t("af:offers.filter.purchase.total_amount.label") }}
              </p>
              <p class="purchase-card-amount">
                {{ activePurchase.totalAmount | monify }} Ft
              </p>
            </div>

            <div class="purchase-card">
              <p class="purchase-card-label">
                {{ $t("af:offers.filter.purchase.down_payment.label") }}
              </p>
              <p class="purchase-card-amount">{{ downPayment | monify }} Ft</p>
            </div>

            <div class="purchase-card" v-if="extraAmount > 0">
              <p class="purchase-card-label">
                {{ $t("af:offers.filter.purchase.extra_amount.label") }}
              </p>
              <p class="purchase-card-amount">{{ extraAmount | monify }} Ft</p>
            </div>
          </div>
        </div>
      </div>

      <section class="af-section sorting-container">
        <div>
          <p class="has-text-grey-icon has-text-weight-medium is-size-14">
            {{ $t("af:offers.filter.subtitle") }}
          </p>

          <div class="is-flex">
            <b-field class="mr-5">
              <b-checkbox v-model="filter.isOnline">
                <b-icon
                  icon-pack="fa"
                  icon="couch"
                  type="is-grey-icon"
                ></b-icon>
                {{ $t("af:offers.filter.input.online.label") }}
              </b-checkbox>
            </b-field>

            <b-field class="mr-5" v-if="hasAdjusted">
              <b-checkbox v-model="filter.isAdjusted">
                <b-icon
                  icon-pack="fa"
                  icon="sliders-h"
                  type="is-grey-icon"
                ></b-icon>
                {{ $t("af:offers.filter.input.adjusted.label") }}
              </b-checkbox>
            </b-field>

            <b-field>
              <b-checkbox v-model="filter.isQuick">
                <b-icon
                  icon-pack="fa"
                  icon="stopwatch"
                  type="is-grey-icon"
                ></b-icon>
                {{ $t("af:offers.filter.input.is_quick.label") }}

                <b-tooltip
                  :label="$t('af:offers.filter.input.is_quick.label')"
                  type="is-tooltip"
                  position="is-right"
                >
                  <b-icon
                    icon-pack="fa"
                    icon="info-circle"
                    type="is-grey-icon"
                  ></b-icon>
                </b-tooltip>
              </b-checkbox>
            </b-field>
          </div>
        </div>

        <div class="is-size-14 has-text-grey-icon">
          <span class="mr-2">{{ $t("af:offers.sort.title") }}</span>

          <b-dropdown aria-role="list" position="is-bottom-left">
            <template #trigger>
              <span class="has-text-weight-medium cursor-pointer" role="button">
                {{ $t(sortBy.label) }}

                <b-icon
                  icon-pack="fa"
                  icon="chevron-down"
                  size="is-small"
                  style="font-size: 10px"
                ></b-icon>
              </span>
            </template>

            <b-dropdown-item
              v-for="sort in availableSortingMethods"
              :key="sort.field"
              aria-role="listitem"
              class="has-text-grey-icon"
              @click="sortBy = sort"
              >{{ $t(sort.label) }}</b-dropdown-item
            >
          </b-dropdown>
        </div>
      </section>

      <OfferLoading v-if="!loaded"></OfferLoading>

      <div v-else class="af-section">
        <Psd2SkippedInfo class="mb-6" v-if="!isPsd2" />

        <EligibleForMoreLoan
          class="mb-6"
          v-if="eligibleMore.duration"
          :dti-calc="eligibleMore"
          @recalc="recalculateDti"
        />

        <NonEligible class="mb-6" v-if="!eligible" />

        <template v-if="offers.length">
          <OfferCard
            v-for="offer in sortedOffers"
            :offer="offer"
            :key="offer.id"
            class="mb-4"
            @dtirecalc="recalculateLowerDti"
          />

          <OffersAreHidden
            :adjusted-exists="hasAdjusted"
            v-if="!filteredOffers.length"
          >
            <p v-if="hasAdjusted && !filter.isAdjusted">
              {{ $t("af:offers.results.label.only_adjusted_results") }}
            </p>
          </OffersAreHidden>
        </template>

        <hr class="my-6" />

        <application-template
          template="TRIPLE_OFFER_BOTTOM"
        ></application-template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { cloneDeep } from "lodash";
import { FIELD_MASK } from "@/utils/const";
import { monify } from "../utils/util";
import merchandiseMixin from "../mixins/merchandiseMixin";
import Psd2SkippedInfo from "../components/psd2/Psd2SkippedInfo";
import OfferAcceptanceModal from "../components/offers/OfferAcceptanceModal";

export default {
  name: "Offers",
  title: "af:page.offers.title",
  mixins: [merchandiseMixin],
  components: {
    Psd2SkippedInfo,
    TermSlider: () => import("@/components/TermSlider.vue"),
    OfferCard: () => import("@/components/offers/OfferCard.vue"),
    EligibleForMoreLoan: () => import("@/components/EligibleForMoreLoan.vue"),
    NonEligible: () => import("@/components/NonEligible.vue"),
    ApplicationTemplate: () => import("@/components/ApplicationTemplate.vue"),
    OfferLoading: () => import("@/components/offers/OfferLoading.vue"),
    BInputWithValidation: () => import("@/components/BInputWithValidation.vue"),
    OffersAreHidden: () => import("@/components/offers/OffersAreHidden.vue"),
  },
  data() {
    return {
      FIELD_MASK,
      filter: {
        isOnline: false,
        isAdjusted: false,
        isQuick: false,
      },
      amount: 0,
      downPayment: 0,
      extraAmount: 0,
      latestDuration: 0,
      latestAmount: 0,
      sortBy: {
        field: "time",
        label: "af:offers.sort.methods.time",
      },
      sorting: [
        {
          field: "thm",
          label: "af:offers.sort.methods.thm",
        },
        {
          field: "monthlyPayment",
          label: "af:offers.sort.methods.monthly_payment",
        },
        {
          field: "time",
          label: "af:offers.sort.methods.time",
        },
      ],
    };
  },
  async created() {
    if (!this.$store.getters["profile/isInState"]("PRELIMINARY")) {
      await this.$router.push(this.$store.getters["profile/getBackPath"]);
      return;
    }
    await this.$store.dispatch("offers/fetchLatest");
    let ids = this.$store.getters["offers/offerIds"];

    await this.$store.dispatch("purchase/fetchActivePurchase");

    if (this.activePurchase) {
      this.downPayment = this.activePurchase.downPayment;
      this.extraAmount = this.activePurchase.extraAmount;
    }

    const promises = [];
    promises.push(this.$store.dispatch("offers/fetchOfferDatas", ids));
    promises.push(this.$store.dispatch("preliminaryAssessment/fetchLatest"));
    promises.push(this.$store.dispatch("filterSettings/fetchFilterSettings"));

    await Promise.all(promises);

    this.latestDuration = this.getDuration;
    this.latestAmount = this.getAmount;

    let offerIdToOpen = this.$route.params.offerIdToOpen;
    if (offerIdToOpen) {
      if (
        this.offers.filter((offer) => offer.data.id === offerIdToOpen).length >
        0
      ) {
        this.$buefy.modal.open({
          parent: this,
          component: OfferAcceptanceModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          props: {
            offerId: offerIdToOpen,
          },
        });
        await this.$store.dispatch("profile/updateCustomerEmailConfirm", {
          offerId: null,
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      loaded: "offers/loaded",
      offers: "offers/offers",
      getAmount: "offers/amount",
      eligible: "offers/eligible",
      eligibleMore: "offers/more",
      getDuration: "offers/duration",
      filterSettings: "filterSettings/get",
      activePurchase: "purchase/getActivePurchase",
      isPsd2: "preliminaryAssessment/isPsd2",
    }),
    hasAdjusted() {
      for (let o in this.offers) {
        if (
          this.offers[o].calculation.compatibility === "ADJUSTED" ||
          this.offers[o].calculation.compatibility === "DTI_ADJUSTED"
        ) {
          return true;
        }
      }
      return false;
    },
    availableSortingMethods() {
      return this.sorting.filter((s) => s.field !== this.sortBy.field);
    },
    sortedOffers() {
      const offers = cloneDeep(this.filteredOffers);
      console.log(offers);

      if (this.sortBy.field === "thm") {
        offers.sort((a, b) => {
          return a.calculation.thm - b.calculation.thm;
        });
      } else if (this.sortBy.field === "monthlyPayment") {
        offers.sort((a, b) => {
          return a.calculation.monthlyPayment - b.calculation.monthlyPayment;
        });
      } else if (this.sortBy.field === "time") {
        offers.sort((a, b) => {
          return a.data.applicationTime - b.data.applicationTime;
        });
      }

      return offers;
    },
    filteredOffers() {
      let offers = this.offers;
      if (this.filter.isOnline) {
        offers = offers.filter((o) => o.data.online);
      }
      if (this.filter.isQuick) {
        offers = offers.filter((o) => o.data.applicationTime <= 1800);
      }
      if (!this.filter.isAdjusted) {
        offers = offers.filter(
          (o) =>
            o.calculation.compatibility !== "ADJUSTED" &&
            o.calculation.compatibility !== "DTI_ADJUSTED"
        );
      }
      return offers;
    },
    duration: {
      get() {
        return this.getDuration;
      },
      set(duration) {
        this.setDuration(duration);
      },
    },
  },
  methods: {
    ...mapMutations({
      setDuration: "offers/setDuration",
      setAmount: "offers/setAmount",
    }),
    adjustAmount(amountInput) {
      amountInput = Number(amountInput);

      if (!isNaN(amountInput)) {
        let adjustedAmount = amountInput;

        if (amountInput % this.filterSettings.amountStep !== 0) {
          adjustedAmount =
            Math.round(amountInput / this.filterSettings.amountStep) *
            this.filterSettings.amountStep;
        }

        if (adjustedAmount < this.filterSettings.amountMin) {
          adjustedAmount = this.filterSettings.amountMin;
        } else if (adjustedAmount > this.filterSettings.amountMax) {
          adjustedAmount = this.filterSettings.amountMax;
        }

        this.setAmount(adjustedAmount);
        this.$refs.amountInput.forceUpdate(adjustedAmount);

        if (this.activePurchase) {
          this.downPayment = this.calculateDownPayment(adjustedAmount);
          this.extraAmount = this.calculateExtraAmount(adjustedAmount);
        }
      }
    },
    recalculateDti() {
      let amount = this.eligibleMore.amount;

      this.setDuration(this.eligibleMore.duration);
      this.setAmount(amount);

      if (this.activePurchase) {
        this.downPayment = this.calculateDownPayment(amount);
        this.extraAmount = this.calculateExtraAmount(amount);
      }

      this.recalculate();
    },
    recalculateLowerDti(dtiParams) {
      let amount = dtiParams.amount;

      this.setDuration(dtiParams.duration);
      this.setAmount(amount);

      if (this.activePurchase) {
        this.downPayment = this.calculateDownPayment(amount);
        this.extraAmount = this.calculateExtraAmount(amount);
      }

      this.recalculate();
    },
    async recalculate() {
      await this.$store.dispatch("preliminaryAssessment/fetchLatest");

      await this.$store.dispatch("preliminaryAssessment/recalculate", {
        duration: this.getDuration,
        amount: this.getAmount,
        downPayment: this.downPayment,
        extraAmount: this.extraAmount,
      });

      let ids = this.$store.getters["offers/offerIds"];
      await this.$store.dispatch("offers/fetchOfferDatas", ids);
    },
    restoreFilters() {
      this.setDuration(this.latestDuration);
      this.setAmount(this.latestAmount);
    },
    retakeAssessment() {
      this.$router.push({
        name: "PreliminaryAssessment",
      });
    },
    monify,
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.offers-header {
  background-image: url("../assets/images/background-sport-car.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  padding: 44px 0 72px;
  position: relative;

  p {
    color: white;
    text-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  }

  .retake-assessment {
    position: absolute;
    top: 1rem;
    left: 1rem;
    display: flex;
    align-items: center;

    .text {
      color: white;
      text-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
    }
  }
}

.af-card {
  padding: 20px;
  margin-top: -24px;
  background: $grey-2;

  &__filter {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }

  .purchase-card-container {
    display: flex;
    flex-direction: column;

    .purchase-card {
      background: #ffffff;
      box-shadow: 0 2px 5px rgba(42, 45, 52, 0.1);
      border-radius: 4px;
      padding: 1rem;
      margin-bottom: 1rem;

      &-label {
        margin-bottom: 0.25rem;
        color: $grey-icon;
        text-transform: uppercase;
        font-size: 10px;
      }

      &-amount {
        font-weight: 500;
        font-size: 1.25rem;
        margin-bottom: 0;
      }
    }
  }
}

.loan-min-max {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.af-section {
  padding: 20px;
}

.required-documents {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem;

  .required-document-tile {
    padding: 26px 24px;
    background: $grey-2;
    border-radius: 8px;
  }
}

@media only screen and (min-width: 769px) {
  .af-card {
    padding: 32px 36px;

    &__filter {
      grid-gap: 2rem;
      grid-template-columns: 1fr 1fr 0.7fr;

      .loan-term-field {
        position: relative;

        &:after {
          content: "";
          position: absolute;
          top: 0;
          right: -1rem;
          height: 100%;
          border: 1px dashed #adb7c4;
        }
      }
    }
  }

  .required-documents {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media only screen and (min-width: 1024px) {
  .af-section {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }

  .af-card {
    border-radius: 12px;

    .purchase-card-container {
      flex-direction: row;

      .purchase-card {
        margin-right: 1rem;
        margin-bottom: 0;
      }
    }
  }

  .sorting-container {
    display: flex;
    justify-content: space-between;
  }
}
</style>
